import React, { useCallback, useEffect, useState } from 'react';

// Store
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { fetchUpcomingInterviews } from 'src/store/lists/upcomingInterview/asyncThunk';

// UI
import { makeStyles } from '@material-ui/core/styles';
import {
  CircularProgress,
  ClickAwayListener,
  Grow,
  IconButton,
  Popper,
} from '@material-ui/core';
import { BaseTypography } from 'src/components/common';
import { NotificationsSection } from './NotificationSection';
// UI - icons
import AccessAlarmIcon from '@material-ui/icons/AccessAlarm';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

// Utils
import { IPlannedInterviewsByFullDate } from 'src/api/base/Interviews/types';
import { msToTime } from 'src/utils/helpers';
import { checkIsLoading } from 'src/utils/types/statuses';

interface IUseStylesProp {
  openedNotificationsAnchorEl: boolean;
}

const useStyles = makeStyles(() => ({
  arrowIcon: {
    marginBottom: '-5.5px',
    cursor: 'pointer',
    transform: (props: IUseStylesProp) =>
      props.openedNotificationsAnchorEl ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: '0.3s transform',
  },
  timerRow: {
    display: 'grid',
    alignItems: 'center',
    gridTemplateColumns: 'auto 1fr',
    gridGap: '5px',
  },
}));

const getInterviewTimestamp = (interview: IPlannedInterviewsByFullDate) =>
  new Date(interview.date).getTime();

const useClosesInterview = () => {
  const dispatch = useDispatch();

  const upcomingInterviewsState = useSelector(
    (state: RootState) => state.upcomingInterviews,
  );

  useEffect(() => {
    dispatch(fetchUpcomingInterviews());
  }, [dispatch]);

  const getClosestInterview = useCallback(() => {
    let closestTimestamp = Infinity;
    let interview;
    const currentDateTimestamp = new Date().getTime();

    upcomingInterviewsState?.data?.forEach((item) => {
      const itemTimestamp = getInterviewTimestamp(item);
      if (
        itemTimestamp < closestTimestamp &&
        itemTimestamp > currentDateTimestamp
      ) {
        interview = item;
        closestTimestamp = itemTimestamp;
      }
    });

    return {
      interview,
    };
  }, [upcomingInterviewsState.data]);

  const [timeToInterview, setTimeToInterview] = useState('00:00');

  useEffect(() => {
    function getReadableTimeToInterview() {
      const closestInterview = getClosestInterview()?.interview;

      if (closestInterview) {
        const difference =
          getInterviewTimestamp(getClosestInterview().interview!) -
          new Date().getTime();
        const { readableHours, readableMinutes, readableDays } =
          msToTime(difference);

        if (Number(readableDays)) {
          setTimeToInterview(
            readableDays > 1 ? `${readableDays} DAYS` : `${readableDays} DAY`,
          );
        } else {
          setTimeToInterview(`${readableHours}:${readableMinutes}`);
        }
      }
    }

    getReadableTimeToInterview();

    // 50 seconds - opinion of developer
    const intervalId = setInterval(getReadableTimeToInterview, 50 * 1000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [getClosestInterview]);

  return {
    timeToInterview,
    upcomingInterviewsState,
  };
};

export const InterviewsNotifications = () => {
  const { timeToInterview, upcomingInterviewsState } = useClosesInterview();

  const [notificationsAnchorEl, setNotificationsAnchorEl] =
    React.useState<null | HTMLElement>(null);
  const handleNotificationsClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(
      notificationsAnchorEl ? null : event.currentTarget,
    );
  };
  const openedNotificationsAnchorEl = Boolean(notificationsAnchorEl);
  const notificationsPopperId = openedNotificationsAnchorEl
    ? 'upcomingInterview'
    : undefined;

  const handleClickAway = (
    event: React.MouseEvent<Document, globalThis.MouseEvent>,
  ) => {
    if (event.target === notificationsAnchorEl) return;
    setNotificationsAnchorEl(null);
  };
	
  const nextInterviewExist = upcomingInterviewsState?.data?.length;

  const classes = useStyles({
    openedNotificationsAnchorEl,
  });

  return (
    <div>
      <div>
        <div className={classes.timerRow}>
          {checkIsLoading(upcomingInterviewsState.status) && (
            <CircularProgress size="15px" />
          )}

          {Boolean(nextInterviewExist) && (
            <BaseTypography style={{ fontWeight: 600, fontSize: '20px' }}>
              <span className="primary">{timeToInterview}</span>
            </BaseTypography>
          )}
          <IconButton onClick={handleNotificationsClick}>
            <AccessAlarmIcon color={nextInterviewExist ? 'primary' : 'disabled'} />
          </IconButton>
        </div>
      </div>

      <Popper
        id={notificationsPopperId}
        open={openedNotificationsAnchorEl}
        anchorEl={notificationsAnchorEl}
        placement="bottom-start"
        style={{ zIndex: 100 }}
        transition>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={300}>
            <div>
              <ClickAwayListener onClickAway={handleClickAway}>
                <div>
                  <NotificationsSection />
                </div>
              </ClickAwayListener>
            </div>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
